import React from 'react';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import {
  HOMEPAGE_CAROUSELS,
  HomepageCarouselId,
  HomepageCarouselConfig,
} from 'src/constants/homepageCarousels';
import { LearningModulesCarousel } from 'src/components/carousel';
import { validateCarouselConfig, validateCarouselWeblab } from 'src/utils/carouselUtils';
import { useAppSelector } from 'src/redux/hooks';
import { WeblabTreatment } from 'src/constants/weblab';

interface HomepageCarouselsSectionProps {
  /** Bundle name for i18n translations */
  bundleName: string;
  /** Optional array of carousel IDs to render. If not provided, all carousels will be rendered */
  carouselIds?: HomepageCarouselId[];
  /** Optional className for the section container */
  className?: string;
  /** Optional callback when a specific carousel loads modules */
  onCarouselLoaded?: (carouselId: HomepageCarouselId, hasModules: boolean) => void;
}

/**
 * Individual carousel wrapper that renders carousel with title and optional description
 */
interface HomepageCarouselWrapperProps {
  carouselId: HomepageCarouselId;
  config: HomepageCarouselConfig;
  title: string;
  description?: string;
  onCarouselLoaded?: (carouselId: HomepageCarouselId, hasModules: boolean) => void;
}

const HomepageCarouselWrapper: React.FC<HomepageCarouselWrapperProps> = ({
  carouselId,
  config,
  title,
  description,
  onCarouselLoaded,
}) => {
  const [shouldHide, setShouldHide] = React.useState(false);
  const [hasNotified, setHasNotified] = React.useState(false);

  const handleEmpty = () => {
    setShouldHide(true);
    setHasNotified(true);
    // Immediately notify parent that carousel is now empty
    onCarouselLoaded?.(carouselId, false);
  };

  // After carousel loads, notify parent of module availability
  React.useEffect(() => {
    if (!hasNotified && onCarouselLoaded) {
      const timer = setTimeout(() => {
        onCarouselLoaded(carouselId, !shouldHide);
        setHasNotified(true);
      }, 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [hasNotified, onCarouselLoaded, shouldHide, carouselId]);

  // Don't render if LearningModulesCarousel signaled it's empty
  if (shouldHide) {
    return null;
  }

  return (
    <section key={carouselId} className="mb-[36px]" id={String(carouselId)}>
      <div className="mx-[28px] !pt-[36px]">
        <h2 className={`${description ? '!mb-su-space-03' : '!mb-su-space-05'} !text-su-heading-400 !font-bold`}>
          {title}
        </h2>
        {description && (
          <p className="!mb-su-space-05 text-su-heading-100">
            {description}
          </p>
        )}
      </div>
      <LearningModulesCarousel
        tagIds={config.tagIds}
        className="mx-[-40px] px-su-space-06"
        onEmpty={handleEmpty}
      />
    </section>
  );
};

/**
 * HomepageCarouselsSection - Renders multiple homepage carousels based on configuration
 *
 * This component iterates through the HOMEPAGE_CAROUSELS configuration and renders
 * each carousel with its translated title. It reuses the existing LearningModulesCarousel
 * component but with tag-based queries instead of subtopic queries.
 *
 * Visibility is determined by:
 * 1. Config validation (valid query type, tagIds present)
 * 2. Per-carousel weblab check (if weblab config is defined)
 * 3. Module existence (API returns modules → render, empty → auto-hide)
 *
 * Marketplace and locale visibility are handled by the backend:
 * - NalandaCMS controls module active/inactive state per marketplace
 * - SUOS filters modules without instances in user's language
 */
const HomepageCarouselsSection: React.FC<HomepageCarouselsSectionProps> = ({
  bundleName,
  carouselIds,
  className = '',
  onCarouselLoaded,
}) => {
  const [bundle] = useBundle(bundleName);
  const weblabsById = useAppSelector((rootState) => rootState.weblabs.byId);

  // Determine which carousels to render
  const carouselsToRender = carouselIds || (Object.keys(HOMEPAGE_CAROUSELS) as HomepageCarouselId[]);

  return (
    <div className={className}>
      {carouselsToRender.map((carouselId) => {
        const config = HOMEPAGE_CAROUSELS[carouselId];

        // Step 1: Config validation (query type, tagIds)
        if (!validateCarouselConfig(config)) {
          return null;
        }

        // Step 2: Per-carousel weblab check
        const userTreatment = config.weblab
          ? weblabsById[config.weblab.weblabId]?.treatment as WeblabTreatment | undefined
          : undefined;

        if (!validateCarouselWeblab(config.weblab, userTreatment)) {
          return null;
        }

        const title = t(bundle, config.title);
        const description = config.description ? t(bundle, config.description) : undefined;

        return (
          <HomepageCarouselWrapper
            key={carouselId}
            carouselId={carouselId}
            config={config}
            title={title}
            description={description}
            onCarouselLoaded={onCarouselLoaded}
          />
        );
      })}
    </div>
  );
};

export default HomepageCarouselsSection;
